import _ from 'lodash';
import { getAesEncryptValToken } from './crypto';
import {
    UK_COUNTRY_CODE,
    EU_COUNTRY_CODE_LIST,
    zoomLevelMappings,
    LIVE_CHAT_PROD_CONFIG,
    LIVE_CHAT_TEST_CONFIG,
    TEST_DATA_ORG_ID,
    PROD_DATA_ORG_ID,
    RewardScenario,
    GlobalPlatFormEnum,
} from '@/constants';
import { authSignIn } from '@/service/api/auth';
import type { ErrorObject, NotFoundConfig, TimeoutPromiseProps } from '@/types';
import { responseType, livechatConfig } from '@/types/liveChat';
import { matchAcceptLang } from '@/service/request';
import {
    APP_TOKEN_KEY,
    DEFAULT_COUNTRY_CODE,
    DEFAULT_LANG,
    ECON,
    ECON_IV,
    ECON_KEY,
    GlobalStateEnum,
    NEW_REQUEST_LANG_MAP,
    REQUEST_HEADERS_LANG_LIST,
    REQUEST_LANG_MAP,
    acceptLang,
    sitesLang,
} from '@/constants/common';

export const parseJson = (jsonInput: string | responseType): responseType => {
    if (typeof jsonInput === 'string') {
        try {
            return JSON.parse(jsonInput);
        } catch (error) {
            throw new Error('Invalid JSON string');
        }
    } else if (typeof jsonInput === 'object') {
        return jsonInput;
    } else {
        return {
            code: 1,
        };
    }
};

export const getWebViewPlatform = (): 'iOS' | 'Android' | 'unknown' => {
    if (typeof navigator === 'undefined') {
        return 'unknown';
    }
    // "UA:" "Mozilla/5.0 (iPhone; CPU iPhone OS 14_5_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 Safari/604.1"
    const userAgent = (navigator.userAgent || navigator.vendor || '').toLowerCase();
    const isIOS = /iphone|ipod|ipad|macintosh/.test(userAgent) && /applewebkit/.test(userAgent);
    const isAndroid = /android/.test(userAgent) && /chrome\/[\d.]+ mobile/.test(userAgent);

    if (isIOS) {
        return 'iOS';
    } else if (isAndroid) {
        return 'Android';
    } else {
        return 'unknown';
    }
};

export const isAndroid = () => {
    const platform = getWebViewPlatform();
    return platform === 'Android';
};

export const isIOS = () => {
    const platform = getWebViewPlatform();
    return platform === 'iOS';
};

export const isEcoflowApp = () => {
    return /EcoflowApp/.test(navigator.userAgent) || /EcoflowAPP/.test(navigator.userAgent);
};

export const isObject = (value: any): boolean => {
    return typeof value === 'object' && value !== null;
};

export const isString = (value: any): boolean => {
    return typeof value === 'string';
};

export const isJSONString = (str: string): boolean => {
    try {
        JSON.parse(str);
        return true;
    } catch (error) {
        return false;
    }
};

export const getLocalStorage = (key: string) => {
    if (typeof window !== 'undefined') {
        const value = localStorage.getItem(key);
        return value ? (isJSONString(value) ? JSON.parse(value) : value) : null;
    }
    return null;
};

export const setLocalStorage = (key: string, value: any) => {
    if (typeof window !== 'undefined') {
        localStorage.setItem(key, isObject(value) ? JSON.stringify(value) : value);
    }
};

export const removeLocalStorage = (key: string) => {
    if (typeof window !== 'undefined') {
        localStorage.removeItem(key);
    }
};

export const getSessionStorage = (key: string) => {
    if (typeof window !== 'undefined') {
        const value = sessionStorage.getItem(key);
        return value ? (isJSONString(value) ? JSON.parse(value) : value) : null;
    }
    return null;
};

export const setSessionStorage = (key: string, value: any) => {
    if (typeof window !== 'undefined') {
        sessionStorage.setItem(key, isObject(value) ? JSON.stringify(value) : value);
    }
};

export const removeSessionStorage = (key: string) => {
    if (typeof window !== 'undefined') {
        sessionStorage.removeItem(key);
    }
};

export const generatorPlayUrl = (rawUrl: string) => {
    try {
        const urlParser = new URL(rawUrl);
        const { hostname, pathname } = urlParser;
        if (!hostname.includes('youtube.com') && !hostname.includes('youtu.be')) return { url: rawUrl, isYouTubeVideo: false };

        let id = '';
        if (pathname.includes('watch')) {
            id = urlParser.searchParams.get('v') || '';
        } else {
            const pathArr = pathname.split('/');
            const length = pathArr?.length;
            if (length) {
                id = pathArr[length - 1] || pathArr[length - 2];
            }
        }
        if (id) {
            return {
                url: `https://www.youtube.com/embed/${id}`,
                imageUrl: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
                isYouTubeVideo: true,
            };
        }
        return { url: rawUrl, isYouTubeVideo: false };
    } catch {
        return { url: rawUrl, isYouTubeVideo: false };
    }
};

export const isJSON = (str: any): boolean => {
    str = typeof str !== 'string' ? JSON.stringify(str) : str;

    try {
        str = JSON.parse(str);
    } catch (e) {
        return false;
    }

    if (typeof str === 'object' && str !== null) {
        return true;
    }
    return false;
};

export const getUrlParams = (url: string) => {
    const urlStr = url.split('?')[1];
    const urlSearchParams = new URLSearchParams(urlStr);
    const result = Object.fromEntries(urlSearchParams.entries());
    return result;
};

export const getForgetUrl = (locale: string): string => {
    return `https://www.ecoflow.com/${locale}/forget`;
};

type Version = {
    major: number;
    minor: number;
    patch: number;
    build?: number;
};

function parseVersion(versionString: string): Version {
    const [major, minor, patch, build] = versionString.split('.').map(Number);
    return { major, minor, patch, build };
}

function compareVersions(versionA: Version, versionB: Version): number {
    if (versionA.major !== versionB.major) {
        return versionA.major - versionB.major;
    }

    if (versionA.minor !== versionB.minor) {
        return versionA.minor - versionB.minor;
    }

    if (versionA.patch !== versionB.patch) {
        return versionA.patch - versionB.patch;
    }

    const buildA = versionA.build ?? 0;
    const buildB = versionB.build ?? 0;

    return buildA - buildB;
}

export const getCompareVersionResult = (currentversion: string, compareVersion: string): number => {
    const version1 = parseVersion(currentversion);
    const version2 = parseVersion(compareVersion);
    const comparisonResult = compareVersions(version1, version2);
    return comparisonResult;
};

export const getIOSVersion = (input: string): string | null => {
    const versionRegex = /(?:\d+\.)+\d+/;
    const match = input.match(versionRegex);
    return match ? match[0] : null;
};

export const getAndroidVersion = (input: string): string | null => {
    const versionRegex = /(?:\d+\.)+\d+(?:-\w+)?/;
    const match = input.match(versionRegex);
    return match ? match[0] : null;
};

export const getCurrentAppVersion = (version: string): string => {
    if (isAndroid()) {
        return getAndroidVersion(version) || '';
    } else if (isIOS()) {
        return getIOSVersion(version) || '';
    }
    return '';
};

export const getAppSystemVersion = (version: string) => {
    let versionRegex = null;
    if (isAndroid()) {
        versionRegex = /(?:\d+\.)+\d+(?:-\w+)?/;
        const match = version.match(versionRegex);
        return match ? match[0] : '0';
    }

    if (isIOS()) {
        versionRegex = /(?:\d+\.)+\d+/;
        const match = version.match(versionRegex);
        return match ? match[0] : '0';
    }

    return '0';
};

export function getBrowserLanguage(): string {
    const language: string = navigator.language || (navigator as any).userLanguage;
    return language;
}

// 定义两个接口来表示不同环境下的域名
interface DevDomain {
    domain: string;
    env: 'dev';
}

interface SitDomain {
    domain: string;
    env: 'sit';
}

// 定义一个函数，用于判断域名属于哪个环境，并返回对应的接口
export function getEnvironment(domain: string): DevDomain | SitDomain {
    if (domain.includes('dev')) {
        return {
            domain,
            env: 'dev',
        };
    } else if (domain.includes('sit')) {
        return {
            domain,
            env: 'sit',
        };
    } else {
        throw new Error('Unknown domain');
    }
}

interface Device {
    productType: number;
    model: number;
}

enum DeviceType {
    MICRO_INVERTER = '微逆设备',
    SOCKET = '插座设备',
    DELTA_SERIES = '移储delta系列',
    RIVER_SERIES = '移储river系列',
    SOLAR_PANEL = '太阳能板',
}

type DeviceStatus =
    | 'A1'
    | 'A2'
    | 'A3'
    | 'A4'
    | 'A5'
    | 'A6'
    | 'A7'
    | 'A8' // 根据 hasMicroInverter 为 true 的情况
    | 'B1'
    | 'B2'
    | 'B3'
    | 'B4'
    | 'B5'
    | 'B6'
    | 'B7'
    | 'B8'
    | 'unknow'; // 根据 hasMicroInverter 为 false 的情况

export function getBoundDevices(devices: Device[]): string[] {
    const deviceTypes: string[] = devices.flatMap((device) => {
        const { productType, model } = device;
        const types: string[] = [];

        if (productType === 75 && model === 1) {
            types.push(DeviceType.MICRO_INVERTER);
        }
        if (productType === 75 && model === 3) {
            types.push(DeviceType.SOCKET);
        }
        if ([13, 14, 15, 80, 81].includes(productType)) {
            types.push(DeviceType.DELTA_SERIES);
        }
        if ([5, 12, 17, 18, 70, 71, 72].includes(productType)) {
            types.push(DeviceType.RIVER_SERIES);
        }
        if (productType === 60 || productType === 30) {
            // productType  === 30 太阳能追踪器也是属于太阳能板
            types.push(DeviceType.SOLAR_PANEL);
        }
        return types;
    });

    return _.uniq(deviceTypes);
}

// 推荐产品规则
export function checkDeviceStatus(boundDevices: string[]): DeviceStatus {
    const deviceSet = new Set(boundDevices);
    const hasMicroInverter = deviceSet.has(DeviceType.MICRO_INVERTER);
    console.log('hasMicroInverter->', hasMicroInverter);
    const hasSolarPanel = deviceSet.has(DeviceType.SOLAR_PANEL);
    console.log('hasSolarPanel->', hasSolarPanel);
    const hasDeltaOrRiverSeries = deviceSet.has(DeviceType.DELTA_SERIES) || deviceSet.has(DeviceType.RIVER_SERIES);
    console.log('hasDeltaOrRiverSeries->', hasDeltaOrRiverSeries);
    const hasSmartPlug = deviceSet.has(DeviceType.SOCKET);
    console.log('hasSmartPlug->', hasSmartPlug);
    if (!hasMicroInverter) {
        if (hasSolarPanel && hasDeltaOrRiverSeries && hasSmartPlug) {
            return 'A1';
        } else if (!hasSolarPanel && hasDeltaOrRiverSeries && hasSmartPlug) {
            return 'A2';
        } else if (hasSolarPanel && !hasDeltaOrRiverSeries && hasSmartPlug) {
            return 'A3';
        } else if (hasSolarPanel && hasDeltaOrRiverSeries && !hasSmartPlug) {
            return 'A4';
        } else if (!hasSolarPanel && !hasDeltaOrRiverSeries && hasSmartPlug) {
            return 'A5';
        } else if (hasSolarPanel && !hasDeltaOrRiverSeries && !hasSmartPlug) {
            return 'A6';
        } else if (!hasSolarPanel && hasDeltaOrRiverSeries && !hasSmartPlug) {
            return 'A7';
        } else if (!hasSolarPanel && !hasDeltaOrRiverSeries && !hasSmartPlug) {
            return 'A8';
        }
    } else {
        if (hasSolarPanel && hasDeltaOrRiverSeries && hasSmartPlug) {
            return 'B1';
        } else if (!hasSolarPanel && hasDeltaOrRiverSeries && hasSmartPlug) {
            return 'B2';
        } else if (hasSolarPanel && !hasDeltaOrRiverSeries && hasSmartPlug) {
            return 'B3';
        } else if (hasSolarPanel && hasDeltaOrRiverSeries && !hasSmartPlug) {
            return 'B4';
        } else if (!hasSolarPanel && !hasDeltaOrRiverSeries && hasSmartPlug) {
            return 'B5';
        } else if (hasSolarPanel && !hasDeltaOrRiverSeries && !hasSmartPlug) {
            return 'B6';
        } else if (!hasSolarPanel && hasDeltaOrRiverSeries && !hasSmartPlug) {
            return 'B7';
        } else if (!hasSolarPanel && !hasDeltaOrRiverSeries && !hasSmartPlug) {
            return 'B8';
        }
    }
    return 'unknow';
}

export const isUKCountryCode = (code: string): boolean => {
    return UK_COUNTRY_CODE === code;
};

export const syncLoginShopify = async ({ locale, returnToNew }: { locale: string; returnToNew: string }) => {
    const efAppInfo = getLocalStorage('efAppInfo') || getLocalStorage('appUserInfo') || {};
    const { code } = efAppInfo;
    const countryCode = code || 'US';
    const isEUCountry = EU_COUNTRY_CODE_LIST.includes(countryCode.toUpperCase());
    return new Promise(async (resolve, reject) => {
        const params = {
            returnToNew,
            url: `https://${isEUCountry ? 'eu' : isUKCountryCode(countryCode.toUpperCase()) ? 'uk' : countryCode.toLowerCase()}.ecoflow.com`,
            locale,
        };
        try {
            const res = await authSignIn(params);
            if (res?.code !== '0') {
                reject(res?.message || res?.statusText);
            } else {
                resolve(res?.data);
            }
        } catch (error) {
            reject(error);
        }
    });
};

export const getWebsiteFromLocale = (locale: string): string => {
    if (locale === 'us') {
        return 'eu';
    } else if (locale === 'fr') {
        return 'fr';
    } else if (locale === 'de') {
        return 'de';
    } else if (locale === 'it') {
        return 'it';
    } else if (locale === 'es') {
        return 'es';
    } else {
        return 'eu';
    }
};

export const getGTMIDFromLocale = (locale: string): string => {
    if (locale === 'us') {
        return 'eu';
    } else if (locale === 'fr') {
        return 'fr';
    } else if (locale === 'de') {
        return 'de';
    } else if (locale === 'it') {
        return 'it';
    } else if (locale === 'es') {
        return 'es';
    } else {
        return 'eu';
    }
};

export const isPhoneNumber = (input: string): boolean => {
    const regex = /^1[0-9]{10}$/; // 以1开头，后跟10个数字
    return regex.test(input);
};

export const getCDNUrl = (Image: string): string => {
    return `${process.env.CDN}${Image}`;
};

// 要计算设备A和设备B之间的距离，并根据距离确定Google地图的缩放级别（zoom），可以使用Haversine公式来计算球面距离，并根据距离范围进行映射关系设置
export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const earthRadius = 6371e3; // 地球半径，单位为米

    const degToRad = (degree: number) => (degree * Math.PI) / 180;
    const deltaLat = degToRad(lat2 - lat1);
    const deltaLon = degToRad(lon2 - lon1);

    const a =
        Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) + Math.cos(degToRad(lat1)) * Math.cos(degToRad(lat2)) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = earthRadius * c;
    return distance;
};

export const calculateZoom = (distance: number): number => {
    for (const mapping of zoomLevelMappings) {
        const [minDistance, maxDistance] = mapping.distanceRange;
        if (distance >= minDistance && distance <= maxDistance) {
            return mapping.zoomLevel;
        }
    }
    return zoomLevelMappings[zoomLevelMappings.length - 1].zoomLevel; // 默认返回最小缩放级别
};

export const isTest = (): boolean => {
    const buildEnv = process.env.NEXT_PUBLIC_BUILD_ENV;
    return buildEnv === 'dev' || buildEnv === 'test';
};

export const getCurrentLiveChatConfig = (): livechatConfig => {
    console.log('isTest->', isTest());
    return isTest() ? LIVE_CHAT_TEST_CONFIG : LIVE_CHAT_PROD_CONFIG;
};

export const getCurrentLiveChatDataOrgId = (): string => {
    console.log('isTest->', isTest());
    return isTest() ? TEST_DATA_ORG_ID : PROD_DATA_ORG_ID;
};

export const errorToObject = (error: any, defaultCode = 'UNKNOWN_ERROR'): ErrorObject => {
    let errorObj: ErrorObject;
    try {
        errorObj = JSON.parse(error);
    } catch (e) {
        errorObj = {
            message: error.message || '服务器异常',
            code: error.code || defaultCode,
        };
    }
    return errorObj;
};

export function getRandomHex(length: number): string {
    let result = '';
    const characters = '0123456789abcdef';
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
}

// traceIDGenerator.ts
export const generateTraceID = (): string => {
    const ts = new Date().getTime().toString(16); // 获取当前时间戳并转换为16进制字符串
    const randomPart = getRandomHex(16); // 生成16位随机十六进制字符串
    const traceID = ts + randomPart; // 将时间戳和随机部分拼接成32位TraceID
    return traceID;
};

type AnyObject = Record<string, any>;

export const removeEmptyValues = (obj: AnyObject): AnyObject => {
    const result: AnyObject = {};
    Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (typeof value === 'object' && value !== null) {
            // 如果值是对象，则递归处理
            const nestedObj = removeEmptyValues(value);
            if (Object.keys(nestedObj).length > 0) {
                result[key] = nestedObj;
            }
        } else if (value !== undefined && value !== '') {
            // 检查值是否为空，可以根据具体需求来判断是否需要去除 undefined 和空字符串
            result[key] = value;
        }
    });
    return result;
};

export function getRewardScenarioValue(source2: keyof typeof RewardScenario): string | undefined {
    const value = RewardScenario[source2];
    return value !== undefined ? value : undefined;
}

export const getFontSize = () => {
    const htmlwidth = document.documentElement.clientWidth || document.body.clientWidth;
    const fontSize = Math.min(200, htmlwidth / 3.9);
    return fontSize;
};

export type QueryParams = { [key: string]: string };

// 获取地址栏参数
export const getQueryString = (name?: string, url?: string): string | QueryParams | null => {
    // 如果url没有传递则取window.location.href
    url = url || window.location.href;

    // 创建一个对象来存储所有的键值对参数
    const params: { [key: string]: string } = {};
    const regex = /[?&]([^=#&]+)=([^&#]*)/g;
    let match;

    // 使用正则表达式来查找所有的键值对
    while ((match = regex.exec(url))) {
        params[decodeURIComponent(match[1])] = decodeURIComponent(match[2].replace(/\+/g, ' '));
    }

    // 如果name未传递则返回所有的键值对参数
    if (!name) {
        return params || {};
    }
    // 否则返回特定name的参数值
    return params[name] !== undefined ? params[name] : null;
};

export const getBrowserLang = () => {
    const browserLanguage: string = navigator.language || 'en-US';
    return browserLanguage;
};

export const matchSiteLang = (): string => {
    const lang: string = getBrowserLang();
    const currentLang: string = acceptLang[lang] || DEFAULT_LANG;
    return currentLang;
};

const getLocalLang = (): string => {
    const lang: string = getLocalStorage('lang') || getQueryString('lang') || matchSiteLang();
    return sitesLang.includes(lang) ? lang : NEW_REQUEST_LANG_MAP[lang] || DEFAULT_LANG;
};

/**
 * @desc 获取当前语言
 * @desc 通过localStorage或地址栏参数
 */
export const getAppCurrentLang = () => {
    // 最新方式通过localStorage
    return getLocalLang();
};

/**
 * @desc 获取当前国家站点
 * @desc 通过localStorage获取code
 */
export const getAppCurrentCountryCode = () => {
    // 最新方式通过localStorage
    return getLocalStorage('code') || DEFAULT_COUNTRY_CODE;
};

export const getAppCurrentVersion = () => {
    // 最新方式通过localStorage

    return getLocalStorage('version') || '';
};

export const getAppCurrentPlatform = (): string => {
    return isAndroid() ? GlobalPlatFormEnum.ANDROID : GlobalPlatFormEnum.IOS;
};

export const getAppAcceptLanguage = (): string => {
    const lang = getLocalLang();
    return matchAcceptLang(lang);
};

export const getAppToken = (): string => {
    const econ = getLocalStorage(ECON);
    return getAesEncryptValToken(econ, ECON_KEY, ECON_IV) || getLocalStorage(GlobalStateEnum.TOKEN) || getLocalStorage(APP_TOKEN_KEY);
};

export type LangType = keyof typeof REQUEST_LANG_MAP;

// 默认请求头带上lang
export const getRequestHeadersLangString = (lang: LangType): string => {
    return REQUEST_HEADERS_LANG_LIST[lang] || 'en-us';
};

export const sleep = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

export const getPathParams = (url: string) => {
    const queryParams = url.split('?')[1];

    if (!queryParams) {
        return {}; // 如果没有问号或者问号后面没有参数，返回空对象
    }

    return queryParams.split('&').reduce((obj: any, param) => {
        const [key, value] = param.split('=');
        obj[key] = value;
        return obj;
    }, {});
};

export const getRedirectPath = (url: string, config: NotFoundConfig): string => {
    console.log('getRedirectPath->url->', url);
    console.log('getRedirectPath->config->', config);
    for (const key in config) {
        if (url.includes(key)) {
            return config[key];
        }
    }
    return '';
};

export const generateTimeoutPromise = ({ ms = 3000, code = 'bridge_timeout' }: TimeoutPromiseProps) => {
    return new Promise((_, reject) => {
        setTimeout(() => {
            reject({
                code,
            });
        }, ms); //设置3秒超时
    });
};
